import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, LayoutGrid, FlaskConical, Eye } from 'lucide-react';
import LabServices from '../../shared/ApiServices/LabServices';
import {
    extractLabOrdersList, extractLabOrderId, extractLabTestsList, extractLabTestId,
    extractPatientName, extractLabSamplesList, normalizeSampleStatus, SAMPLE_STATUS_META,
    pickLabColor,
} from './labUtils';

type QueueSampleStatus = ReturnType<typeof normalizeSampleStatus> | 'untracked';

interface QueueItem {
    orderId: string;
    patientName: string;
    testId: string;
    testName: string;
    department: string;
    sampleStatus: QueueSampleStatus;
}

const UNTRACKED_META = { label: 'No Sample Tracked', bg: 'rgba(107,114,128,0.08)', color: '#6B7280' };
const queueStatusMeta = (s: QueueSampleStatus) => (s === 'untracked' ? UNTRACKED_META : SAMPLE_STATUS_META[s]);

// Sample Routing / department worklist — groups in-progress orders' tests by department
// (real category data from the catalog), showing each test's real specimen status. Read-only,
// no new persistence — a view over LabServices.listOrders()/getSamplesForOrder().
export const DepartmentQueue: React.FC = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState<QueueItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeDept, setActiveDept] = useState('all');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ordersRes, testsRes] = await Promise.all([
                LabServices.listOrders({ status: 'processing', limit: 40 }),
                LabServices.getLabTests(),
            ]);
            const orders = extractLabOrdersList(ordersRes);
            const catalog = extractLabTestsList(testsRes);
            const categoryByTestId = new Map(catalog.map(t => [extractLabTestId(t), t.category || 'Other']));

            const perOrderSamples = await Promise.all(
                orders.map(o => LabServices.getSamplesForOrder(extractLabOrderId(o)).catch(() => []))
            );

            const result: QueueItem[] = [];
            orders.forEach((order, idx) => {
                const orderId = extractLabOrderId(order);
                const orderSamples = extractLabSamplesList(perOrderSamples[idx]);
                (order.tests ?? []).forEach(t => {
                    const testId = t.testId ?? (t as any).test_id ?? '';
                    const sample = orderSamples.find(s => (s.testId ?? (s as any).test_id) === testId);
                    result.push({
                        orderId,
                        patientName: extractPatientName(order),
                        testId,
                        testName: t.testName ?? (t as any).test_name ?? 'Test',
                        department: categoryByTestId.get(testId) || 'Other',
                        sampleStatus: sample ? normalizeSampleStatus(sample) : 'untracked',
                    });
                });
            });
            setItems(result);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const departments = Array.from(new Set(items.map(i => i.department))).sort();
    const visible = activeDept === 'all' ? items : items.filter(i => i.department === activeDept);
    const grouped = visible.reduce<Record<string, QueueItem[]>>((acc, item) => {
        (acc[item.department] ??= []).push(item);
        return acc;
    }, {});

    return (
        <div className="flex flex-col gap-4 v2-animate-fade-in-up">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex flex-col gap-0.5">
                    <h1 className="text-[18px] font-black text-[var(--v2-text-heading)] m-0 tracking-tight">Department Queue</h1>
                    <p className="text-[11px] text-[var(--v2-text-muted)] m-0">Tests from in-progress orders, routed by department</p>
                </div>
                <button type="button" onClick={load} disabled={loading}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)] disabled:opacity-50 transition-colors">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} /> Refresh
                </button>
            </div>

            {/* Department filter pills */}
            <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={() => setActiveDept('all')}
                    className="px-3 py-1.5 rounded-full text-[11px] font-bold cursor-pointer border transition-all"
                    style={activeDept === 'all'
                        ? { background: '#059669', borderColor: '#059669', color: '#fff' }
                        : { background: 'var(--v2-bg-surface)', borderColor: 'var(--v2-border-card)', color: 'var(--v2-text-muted)' }}>
                    All ({items.length})
                </button>
                {departments.map(dept => {
                    const count = items.filter(i => i.department === dept).length;
                    const color = pickLabColor(dept);
                    return (
                        <button key={dept} type="button" onClick={() => setActiveDept(dept)}
                            className="px-3 py-1.5 rounded-full text-[11px] font-bold cursor-pointer border transition-all"
                            style={activeDept === dept
                                ? { background: color, borderColor: color, color: '#fff' }
                                : { background: 'var(--v2-bg-surface)', borderColor: 'var(--v2-border-card)', color: 'var(--v2-text-muted)' }}>
                            {dept} ({count})
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-24">
                    <div className="w-6 h-6 rounded-full border-2 border-[#059669] border-t-transparent animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-20 text-center rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)]" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                    <LayoutGrid className="w-8 h-8 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                    <p className="text-[13px] font-semibold text-[var(--v2-text-secondary)]">No in-progress orders</p>
                    <p className="text-[11px] text-[var(--v2-text-muted)]">Orders move here once marked "Processing"</p>
                </div>
            ) : (
                <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                    {Object.entries(grouped).map(([dept, deptItems]) => {
                        const color = pickLabColor(dept);
                        return (
                            <div key={dept} className="rounded-2xl border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                                <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${color} 0%, ${color}99 100%)` }} />
                                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--v2-border-subtle)]">
                                    <div className="flex items-center gap-2">
                                        <FlaskConical className="w-4 h-4" style={{ color }} strokeWidth={2} />
                                        <p className="text-[12px] font-bold text-[var(--v2-text-heading)] m-0">{dept}</p>
                                    </div>
                                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: `${color}18`, color }}>
                                        {deptItems.length}
                                    </span>
                                </div>
                                <div className="flex flex-col divide-y divide-[var(--v2-border-divider)] max-h-80 overflow-y-auto">
                                    {deptItems.map((item, i) => {
                                        const meta = queueStatusMeta(item.sampleStatus);
                                        return (
                                            <div key={`${item.orderId}-${item.testId}-${i}`}
                                                onClick={() => navigate(`/lab/processing?orderId=${encodeURIComponent(item.orderId)}`)}
                                                className="flex items-center justify-between gap-2 px-4 py-2.5 cursor-pointer hover:bg-[var(--v2-bg-surface)] transition-colors">
                                                <div className="min-w-0">
                                                    <p className="text-[11px] font-semibold text-[var(--v2-text-primary)] m-0 truncate">{item.testName}</p>
                                                    <p className="text-[10px] text-[var(--v2-text-muted)] m-0 truncate">{item.patientName}</p>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span className="text-[9px] font-black px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: meta.bg, color: meta.color }}>
                                                        {meta.label}
                                                    </span>
                                                    <Eye className="w-3 h-3 text-[var(--v2-text-muted)]" strokeWidth={2} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DepartmentQueue;
